// hooks/useCompanyDashboard.js
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getCompanyDashboardAnalytics,
  getCompanyFundsAnalytics,
  getCompanyEmployeesAnalytics,
  getCompanyLoansAnalytics,
  getCompanyContributionTrends,
  getTopEmployeesAnalytics,
} from "../stores/services/company.service";

export const useCompanyDashboard = () => {
  const email = sessionStorage.getItem("email") || "";

  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [period, setPeriod] = useState("30d");
  const [customDateRange, setCustomDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Analytics data states
  const [dashboardData, setDashboardData] = useState(null);
  const [fundsAnalytics, setFundsAnalytics] = useState<any>([]);
  const [employeesAnalytics, setEmployeesAnalytics] = useState([]);
  const [loansAnalytics, setLoansAnalytics] = useState(null);
  const [contributionTrends, setContributionTrends] = useState(null);
  const [topEmployees, setTopEmployees] = useState(null);

  const [activeTab, setActiveTab] = useState("overview");

  // Fetch Dashboard Analytics
  const fetchDashboardAnalytics = useCallback(async () => {
    if (!selectedCompany) {
      toast.warning("Please select a company first");
      return;
    }

    setLoading(true);
    try {
      const params = {
        email,
        company_code: selectedCompany.value,
        period,
        ...(period === "custom" && customDateRange),
      };

      const response = await getCompanyDashboardAnalytics(params);

      if (response.data.status === 200) {
        setDashboardData(response.data.data);
      } else {
        toast.error(response.data.message || "Failed to load dashboard");
      }
    } catch (error: any) {
      console.error("Error fetching dashboard:", error);
      toast.error(error?.response?.data?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, period, customDateRange, email]);

  // Fetch Funds Analytics
  const fetchFundsAnalytics = useCallback(
    async (symbolCode) => {
      if (!selectedCompany) return;

      try {
        const params = {
          email,
          company_code: selectedCompany.value,
          symbol_code: symbolCode,
          ...(customDateRange.start_date && customDateRange),
        };

        const response = await getCompanyFundsAnalytics(params);

        if (response.data.status === 200) {
          setFundsAnalytics((prev) => [...prev, response.data.data]);
        }
      } catch (error) {
        console.error("Error fetching funds analytics:", error);
        toast.error("Failed to load fund analytics");
      }
    },
    [selectedCompany, customDateRange, email]
  );

  // Fetch Employees Analytics
  const fetchEmployeesAnalytics = useCallback(
    async (searchQuery = "") => {
      if (!selectedCompany) return;

      try {
        const params = {
          email,
          company_code: selectedCompany.value,
          ...(searchQuery && { search: searchQuery }),
        };

        const response = await getCompanyEmployeesAnalytics(params);

        if (response.data.status === 200) {
          setEmployeesAnalytics(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching employees analytics:", error);
        toast.error("Failed to load employees analytics");
      }
    },
    [selectedCompany, email]
  );

  // Fetch Loans Analytics
  const fetchLoansAnalytics = useCallback(async () => {
    if (!selectedCompany) return;

    try {
      const params = {
        email,
        company_code: selectedCompany.value,
        period,
        ...(period === "custom" && customDateRange),
      };

      const response = await getCompanyLoansAnalytics(params);

      if (response.data.status === 200) {
        setLoansAnalytics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching loans analytics:", error);
      toast.error("Failed to load loans analytics");
    }
  }, [selectedCompany, period, customDateRange, email]);

  // Fetch Contribution Trends
  const fetchContributionTrends = useCallback(async () => {
    if (!selectedCompany) return;

    try {
      const params = {
        email,
        company_code: selectedCompany.value,
        period,
        ...(period === "custom" && customDateRange),
      };

      const response = await getCompanyContributionTrends(params);

      if (response.data.status === 200) {
        setContributionTrends(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching contribution trends:", error);
      toast.error("Failed to load contribution trends");
    }
  }, [selectedCompany, period, customDateRange, email]);

  // Fetch Top Employees
  const fetchTopEmployees = useCallback(async () => {
    if (!selectedCompany) return;

    try {
      const params = {
        email,
        company_code: selectedCompany.value,
        period,
        ...(period === "custom" && customDateRange),
      };

      const response = await getTopEmployeesAnalytics(params);

      if (response.data.status === 200) {
        setTopEmployees(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching top employees:", error);
      toast.error("Failed to load top employees");
    }
  }, [selectedCompany, period, customDateRange, email]);

  // Fetch all analytics
  const fetchAllAnalytics = useCallback(async () => {
    if (!selectedCompany) {
      toast.warning("Please select a company first");
      return;
    }

    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardAnalytics(),
        fetchLoansAnalytics(),
        fetchContributionTrends(),
        fetchTopEmployees(),
      ]);
    } catch (error) {
      console.error("Error fetching all analytics:", error);
      toast.error("Failed to load some analytics");
    } finally {
      setLoading(false);
    }
  }, [
    selectedCompany,
    fetchDashboardAnalytics,
    fetchLoansAnalytics,
    fetchContributionTrends,
    fetchTopEmployees,
  ]);

  // Reset data when company changes
  useEffect(() => {
    if (selectedCompany) {
      setDashboardData(null);
      setFundsAnalytics([]);
      setEmployeesAnalytics([]);
      setLoansAnalytics(null);
      setContributionTrends(null);
      setTopEmployees(null);
    }
  }, [selectedCompany]);

  return {
    // State
    loading,
    selectedCompany,
    period,
    customDateRange,
    activeTab,
    dashboardData,
    fundsAnalytics,
    employeesAnalytics,
    loansAnalytics,
    contributionTrends,
    topEmployees,

    // Setters
    setSelectedCompany,
    setPeriod,
    setCustomDateRange,
    setActiveTab,

    // Methods
    fetchDashboardAnalytics,
    fetchFundsAnalytics,
    fetchEmployeesAnalytics,
    fetchLoansAnalytics,
    fetchContributionTrends,
    fetchTopEmployees,
    fetchAllAnalytics,
  };
};
